import React, { useEffect, useRef } from "react";
import useSearchLabel from "../../hooks/useSearchLabel";
import LoadingSpinner from "~/ui/LoadingSpinner/LoadingSpinner";
import { useTranslation } from "react-i18next";
import { XCircleIcon } from "@heroicons/react/24/solid";
import useKeywordSearch from "../../hooks/useKeywordSearch";
import clsx from "clsx";
import { useHelperStore } from "~/stores";
import { SEARCH_VIEW_MODE } from "~/types/search.types";
import SearchPanelMessage from "~/components/SearchPanelMessage";

interface KeywordInputModalProps {
    setTextValue: React.Dispatch<React.SetStateAction<string>>;
}

const KeywordInputModal = ({ setTextValue }: KeywordInputModalProps) => {
    const { t } = useTranslation();
    const viewMode = useHelperStore(state => state.viewMode);
    const { textLabel } = useSearchLabel();
    const placeholder = textLabel ?? "";
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const {
        isLoading,
        inputRef,
        handleInputChange,
        handleSearch,
        handleBtnClick,
        handleClearMessage,
        searchMsg,
        showMsg,
        isSearchEnabled
    } = useKeywordSearch();

    const btnBaseClass =
        "flex justify-center !min-w-[81px] py-2 px-4 text-center !text-base !text-stone-lightest font-medium-light !tracking-[-0.32px] !bg-salmon-primary !rounded-lg";
    const btnClass = clsx(btnBaseClass, (isLoading || !isSearchEnabled) && "opacity-50");
    const btnContent = isLoading ? <LoadingSpinner /> : <span>{t("searchForm.popup.btnSearchLabel")}</span>;
    const VARIABLE_NAME_IN_MS = 300;

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            handleInputChange(e);
        }, VARIABLE_NAME_IN_MS);
    };

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const handleRemoveInputTrue = () => {
        handleBtnClick(true);
    };

    const handleRemoveInputFalse = () => {
        handleBtnClick(false);
    };

    const handleSearchButtonClick = () => {
        if (inputRef.current) {
            setTextValue(inputRef.current.value);
        }
        handleSearch();
    };

    return (
        <>
            <div className="pb-4 px-4">
                <form className="relative w-full h-17">
                    <input
                        type="text"
                        className="w-full py-3.5 px-4 rounded-2xl bg-stone-lighter text-base text-stone-darkest font-medium-light outline-none outline-stone-medium border-red-500"
                        placeholder={placeholder}
                        ref={inputRef}
                        onChange={handleInput}
                        onClick={handleRemoveInputTrue}
                    />
                    {viewMode === SEARCH_VIEW_MODE.KEYWORD && (
                        <button
                            type="button"
                            className="absolute justify-center right-2 top-1/2 -translate-y-1/2 px-4 py-1"
                            onClick={handleRemoveInputFalse}
                        >
                            <XCircleIcon className="w-4 h-4 text-stone-vibrant" />
                        </button>
                    )}
                </form>
            </div>
            <div className="flex w-full justify-between px-4">
                <div className="flex items-center h-full">
                    {showMsg ? (
                        <SearchPanelMessage
                            {...searchMsg}
                            show={showMsg}
                            onClearTrigger={handleClearMessage}
                        />
                    ) : (
                        <div />
                    )}
                </div>
                <div className="flex justify-end py-3">
                    <button
                        onClick={handleSearchButtonClick}
                        disabled={!isSearchEnabled}
                        className={btnClass}
                    >
                        {btnContent}
                    </button>
                </div>
            </div>
        </>
    );
};

export default KeywordInputModal;
